import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  Popover,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  AvatarGroup,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Accordion
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  AccountCircle as UserIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  Sell as SellIcon,
  Engineering as EngineeringIcon,
  ManageAccounts as ManageAccountsIcon,
  WorkOutline as WorkOutlineIcon,
  HighQuality as HighQualityIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const GestionRoles = () => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [screens, setScreens] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  
  // Los permisos se actualizarán en el próximo login
  const [selectedRole, setSelectedRole] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [roleStatistics, setRoleStatistics] = useState(null);

  // Datos por defecto vacíos (sin mock data)
  const emptyRoles = [];

  const mockPermissions = [
    {
      id: 1,
      name: 'create_quotations',
      displayName: 'Crear Cotizaciones',
      description: 'Permite crear nuevas cotizaciones',
      category: 'Cotizaciones',
      isSystem: true
    },
    {
      id: 2,
      name: 'manage_users',
      displayName: 'Gestionar Usuarios',
      description: 'Crear, editar y eliminar usuarios',
      category: 'Usuarios',
      isSystem: true
    },
    {
      id: 3,
      name: 'view_reports',
      displayName: 'Ver Reportes',
      description: 'Acceso a todos los reportes del sistema',
      category: 'Reportes',
      isSystem: true
    },
    {
      id: 4,
      name: 'system_config',
      displayName: 'Configuración del Sistema',
      description: 'Acceso a configuraciones del sistema',
      category: 'Sistema',
      isSystem: true
    },
    {
      id: 5,
      name: 'manage_roles',
      displayName: 'Gestionar Roles',
      description: 'Crear y modificar roles y permisos',
      category: 'Seguridad',
      isSystem: true
    },
    {
      id: 6,
      name: 'export_data',
      displayName: 'Exportar Datos',
      description: 'Exportar información del sistema',
      category: 'Datos',
      isSystem: false
    },
    {
      id: 7,
      name: 'approve_quotations',
      displayName: 'Aprobar Cotizaciones',
      description: 'Aprobar o rechazar cotizaciones',
      category: 'Cotizaciones',
      isSystem: false
    },
    {
      id: 8,
      name: 'manage_team',
      displayName: 'Gestionar Equipo',
      description: 'Gestionar miembros del equipo',
      category: 'Usuarios',
      isSystem: false
    },
    {
      id: 9,
      name: 'view_own_reports',
      displayName: 'Ver Reportes Propios',
      description: 'Ver solo reportes propios',
      category: 'Reportes',
      isSystem: false
    }
  ];

  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    color: '#1976d2',
    permissions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Cargar roles reales desde la base de datos
      try {
        console.log('🔍 Cargando roles desde la base de datos...');
        console.log('🌐 URL del endpoint:', '/users/roles');
        
        // Verificar autenticación
        const token = sessionStorage.getItem('authToken');
        console.log('🔑 Token disponible:', token ? 'SÍ' : 'NO');
        
        // Primero verificar si el endpoint base funciona
        console.log('🧪 Probando endpoint base /users...');
        try {
          const baseTest = await api.get('/users');
          console.log('✅ Endpoint base /users funciona:', baseTest.status);
        } catch (baseError) {
          console.log('❌ Endpoint base /users falla:', baseError.message);
          console.log('❌ Base Status:', baseError.response?.status);
          console.log('❌ Base Data:', baseError.response?.data);
        }
        
        // Probar endpoint de prueba específico
        console.log('🧪 Probando endpoint de prueba /users/test-roles...');
        try {
          const testResponse = await api.get('/users/test-roles');
          console.log('✅ Endpoint de prueba funciona:', testResponse.status, testResponse.data);
        } catch (testError) {
          console.log('❌ Endpoint de prueba falla:', testError.message);
          console.log('❌ Test Status:', testError.response?.status);
          console.log('❌ Test Data:', testError.response?.data);
        }
        
        const rolesResponse = await api.get('/users/roles');
        console.log('📊 Status de respuesta:', rolesResponse.status);
        console.log('📊 Roles recibidos:', rolesResponse.data);
        console.log('📊 Cantidad de roles:', rolesResponse.data?.length || 0);
        
        // Cargar permisos reales para cada rol desde role_screens
        const rolesWithPermissions = await Promise.all(
          rolesResponse.data.map(async (role) => {
            try {
              console.log(`🔍 Cargando permisos para rol: ${role.name} (ID: ${role.id})`);
              const permissionsResponse = await api.get(`/roles/${role.id}/permissions`);
              console.log(`✅ Permisos cargados para ${role.name}:`, permissionsResponse.data);
              
              // Extraer las rutas de las pantallas con permiso de lectura
              const rolePermissions = permissionsResponse.data
                .filter(perm => perm.canRead)
                .map(perm => perm.screen?.route || perm.screenRoute)
                .filter(route => route); // Filtrar valores null/undefined
              
              console.log(`📋 Rutas con acceso para ${role.name}:`, rolePermissions);
              
              return {
                id: role.id,
                name: role.name,
                description: role.description,
                color: role.color,
                icon: role.icon,
                userCount: role.userCount,
                permissions: rolePermissions,
                permissionsDetail: permissionsResponse.data, // Guardar detalle completo
                users: (role.users || []).slice(0, 3).map(user => ({
                  id: user.id,
                  name: `${user.firstName || ''} ${user.firstLastName || ''}`.trim(),
                  avatar: `${user.firstName?.charAt(0) || role.name.charAt(0)}${user.firstLastName?.charAt(0) || role.name.charAt(1)}`
                })),
                // Tomamos el prefijo de fecha directo del string -- convertir a Date y de
                // vuelta a toISOString() desplaza el día (pasa a UTC) en huso horario de México.
                createdAt: role.createdAt ? role.createdAt.substring(0, 10) : '2023-01-01',
                isSystem: role.isSystem || false
              };
            } catch (permError) {
              console.error(`❌ Error al cargar permisos para rol ${role.name}:`, permError);
              // Si falla, devolver el rol sin permisos
              return {
                id: role.id,
                name: role.name,
                description: role.description,
                color: role.color,
                icon: role.icon,
                userCount: role.userCount,
                permissions: [],
                permissionsDetail: [],
                users: (role.users || []).slice(0, 3).map(user => ({
                  id: user.id,
                  name: `${user.firstName || ''} ${user.firstLastName || ''}`.trim(),
                  avatar: `${user.firstName?.charAt(0) || role.name.charAt(0)}${user.firstLastName?.charAt(0) || role.name.charAt(1)}`
                })),
                // Tomamos el prefijo de fecha directo del string -- convertir a Date y de
                // vuelta a toISOString() desplaza el día (pasa a UTC) en huso horario de México.
                createdAt: role.createdAt ? role.createdAt.substring(0, 10) : '2023-01-01',
                isSystem: role.isSystem || false
              };
            }
          })
        );
        
        const realRoles = rolesWithPermissions;
        
        console.log('✅ Roles transformados:', realRoles);
        setRoles(realRoles);
        setSuccess(`✅ Se cargaron ${realRoles.length} roles desde la base de datos`);
        
      } catch (apiError) {
        console.error('❌ Error al cargar roles de la API:', apiError);
        console.error('❌ Status:', apiError.response?.status);
        console.error('❌ Data:', apiError.response?.data);
        console.error('❌ URL:', apiError.config?.url);
        setError(`Error al cargar roles desde la base de datos: ${apiError.message} (Status: ${apiError.response?.status})`);
        setRoles(emptyRoles);
      }
      
      // Cargar permisos reales desde la base de datos
      try {
        console.log('🔍 Cargando permisos del sistema...');
        const permissionsResponse = await api.get('/role-permissions');
        console.log('📊 Permisos recibidos:', permissionsResponse.data);
        
        setRolePermissions(permissionsResponse.data.roles || []);
        
        // Cargar pantallas disponibles
        const screensResponse = await api.get('/role-permissions/screens');
        console.log('📱 Pantallas recibidas:', screensResponse.data);
        setScreens(screensResponse.data || []);
        
        // Transformar pantallas a formato de permisos para compatibilidad
        const screenPermissions = screensResponse.data.map(screen => ({
          id: screen.id,
          name: screen.route,
          displayName: screen.name,
          description: screen.description,
          category: screen.icon === 'admin' ? 'Sistema' : 
                   screen.icon === 'assignment' ? 'Cotizaciones' :
                   screen.icon === 'people' ? 'Usuarios' :
                   screen.icon === 'visibility' ? 'Reportes' : 'General',
          isSystem: screen.route.includes('/admin') || screen.route.includes('/system')
        }));
        
        setPermissions(screenPermissions);
        console.log('✅ Se cargaron permisos del sistema correctamente');
        
      } catch (permError) {
        console.error('❌ Error al cargar permisos:', permError);
        setPermissions(mockPermissions); // Fallback a mock solo si falla
      }
      
      setLoading(false);
    } catch (err) {
      console.error('❌ Error general:', err);
      setError('Error al cargar los datos de roles y permisos');
      setRoles(emptyRoles);
      setPermissions(mockPermissions);
      setLoading(false);
    }
  };

  // Función eliminada: getPermissionsForRole() - Ahora se obtienen permisos reales desde role_screens

  const handleCreateRole = () => {
    setSelectedRole(null);
    setNewRole({
      name: '',
      description: '',
      color: '#1976d2',
      permissions: []
    });
    setEditMode(true);
    setRoleDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setNewRole({
      name: role.name,
      description: role.description,
      color: role.color,
      permissions: role.permissions
    });
    setEditMode(true);
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      setError('');
      
      // Simular guardado
      setTimeout(() => {
        setSuccess(selectedRole ? 'Rol actualizado exitosamente' : 'Rol creado exitosamente');
        setRoleDialogOpen(false);
        setEditMode(false);
        loadData();
      }, 1000);
    } catch (err) {
      setError('Error al guardar el rol');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('¿Está seguro de eliminar este rol?')) {
      try {
        setError('');
        
        // Simular eliminación
        setTimeout(() => {
          setSuccess('Rol eliminado exitosamente');
          loadData();
        }, 1000);
      } catch (err) {
        setError('Error al eliminar el rol');
      }
    }
  };

  const handlePermissionChange = (permissionName, checked) => {
    setNewRole(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permissionName]
        : prev.permissions.filter(p => p !== permissionName)
    }));
  };

  const getRoleIcon = (iconType) => {
    switch (iconType) {
      case 'admin': return <AdminIcon />;
      case 'supervisor': return <SupervisorIcon />;
      case 'user': return <UserIcon />;
      default: return <GroupIcon />;
    }
  };

  // Función para actualizar permisos
  const handlePermissionUpdate = async (roleId, screenId, permissionType, value) => {
    console.log(`🚀 INICIO handlePermissionUpdate`);
    console.log(`   📥 Parámetros recibidos:`, { roleId, screenId, permissionType, value });
    
    try {
      // Encontrar información del rol y pantalla para logging
      const role = roles.find(r => r.id === roleId);
      const screen = screens.find(s => s.id === screenId);
      
      console.log(`🔄 Actualizando permiso:`);
      console.log(`   👤 Rol: ${role?.name} (ID: ${roleId})`);
      console.log(`   📱 Pantalla: ${screen?.name} - ${screen?.route} (ID: ${screenId})`);
      console.log(`   🔧 Permiso: ${permissionType} = ${value}`);
      console.log(`   🔧 Tipo de valor:`, typeof value);
      
      // Encontrar los permisos actuales
      const rolePermission = rolePermissions.find(rp => rp.id === roleId);
      const screenPermission = rolePermission?.screens?.find(s => s.screenId === screenId);
      
      console.log(`   📊 Permisos actuales:`, screenPermission);
      
      // Crear objeto de permisos actualizado
      const updatedPermissions = {
        canRead: screenPermission?.canRead || false,
        canWrite: screenPermission?.canWrite || false,
        canDelete: screenPermission?.canDelete || false,
        canAdmin: screenPermission?.canAdmin || false,
        [permissionType]: value
      };
      
      console.log(`   📝 Permisos nuevos:`, updatedPermissions);
      
      // SIEMPRE ACTUALIZAR - NUNCA ELIMINAR EL REGISTRO
      // Esto mantiene la integridad de la matriz de permisos
      console.log(`📝 Actualizando permisos (manteniendo registro en BD)...`);
      
      // Preparar payload para el backend
      const payload = {
        roleId,
        screenId,
        ...updatedPermissions
      };
      
      console.log(`📤 Enviando al backend:`, payload);
      console.log(`🌐 URL: PUT /role-permissions`);
      
      // Enviar actualización al backend
      const response = await api.put('/role-permissions', payload);
      
      console.log('✅ Respuesta del backend:', response.status, response.data);
      console.log('✅ Permiso actualizado exitosamente');
      
      // Actualizar estado local
      setRolePermissions(prevRolePermissions => {
          return prevRolePermissions.map(rp => {
            if (rp.id === roleId) {
              const updatedScreens = rp.screens.map(s => {
                if (s.screenId === screenId) {
                  return { ...s, [permissionType]: value };
                }
                return s;
              });
              
              // Si no existe la pantalla, agregarla
              if (!rp.screens.find(s => s.screenId === screenId)) {
                updatedScreens.push({
                  screenId,
                  ...updatedPermissions
                });
              }
              
              return { ...rp, screens: updatedScreens };
            }
            return rp;
          });
      });
      
      const action = value ? '✅ Otorgado' : '❌ Revocado';
      setSuccess(`${action}: ${permissionType} para ${role?.name} en ${screen?.name}. Los cambios se aplicarán al iniciar sesión nuevamente.`);
      
      // Los cambios se aplicarán en el próximo login
      console.log('💡 Cambios guardados. Se aplicarán al iniciar sesión nuevamente.');
      
    } catch (error) {
      console.error('🚨 ERROR EN handlePermissionUpdate:');
      console.error('❌ Error completo:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Status Text:', error.response?.statusText);
      console.error('❌ Data:', error.response?.data);
      console.error('❌ Config:', error.config);
      console.error('❌ Request URL:', error.config?.url);
      console.error('❌ Request Method:', error.config?.method);
      console.error('❌ Request Data:', error.config?.data);
      
      setError(`Error al actualizar permiso: ${error.response?.data?.message || error.message} (Status: ${error.response?.status})`);
    } finally {
      console.log(`🏁 FIN handlePermissionUpdate`);
    }
  };

  // Componente para celda de permisos
  const PermissionCell = ({ roleId, screenId, permissions, onPermissionChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
      setAnchorEl(null);
    };
    
    const hasAnyPermission = permissions.canRead || permissions.canWrite || permissions.canDelete || permissions.canAdmin;
    
    return (
      <Box>
        <IconButton 
          onClick={handleClick}
          sx={{ 
            color: hasAnyPermission ? 'success.main' : 'grey.400',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          {hasAnyPermission ? <CheckCircleIcon /> : <CancelCircleIcon />}
        </IconButton>
        
        <Dialog open={Boolean(anchorEl)} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Permisos de Acceso</DialogTitle>
          <DialogContent>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.canRead}
                    onChange={(e) => onPermissionChange(roleId, screenId, 'canRead', e.target.checked)}
                  />
                }
                label="Lectura - Puede ver la pantalla"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.canWrite}
                    onChange={(e) => onPermissionChange(roleId, screenId, 'canWrite', e.target.checked)}
                  />
                }
                label="Escritura - Puede crear y editar"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.canDelete}
                    onChange={(e) => onPermissionChange(roleId, screenId, 'canDelete', e.target.checked)}
                  />
                }
                label="Eliminación - Puede eliminar registros"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.canAdmin}
                    onChange={(e) => onPermissionChange(roleId, screenId, 'canAdmin', e.target.checked)}
                  />
                }
                label="Administración - Acceso completo"
              />
            </FormGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Cotizaciones': return <AssignmentIcon />;
      case 'Usuarios': return <PersonIcon />;
      case 'Reportes': return <VisibilityIcon />;
      case 'Sistema': return <SettingsIcon />;
      case 'Seguridad': return <SecurityIcon />;
      case 'Datos': return <BuildIcon />;
      default: return <SettingsIcon />;
    }
  };

  const groupPermissionsByCategory = () => {
    const grouped = {};
    permissions.forEach(permission => {
      if (!grouped[permission.category]) {
        grouped[permission.category] = [];
      }
      grouped[permission.category].push(permission);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1400px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '"🔐"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Gestión Avanzada de Roles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateRole}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Crear Rol
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}


      {/* Roles Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {roles.length === 0 && !loading ? (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No se encontraron roles en la base de datos
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Verifica que el backend esté funcionando y que existan roles en la tabla 'roles'
              </Typography>
            </Card>
          </Grid>
        ) : (
          roles.map((role) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={role.id}>
            <Card sx={{ 
              height: '100%',
              border: `2px solid ${role.color}20`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ backgroundColor: role.color, mr: 2 }}>
                    {getRoleIcon(role.icon)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {role.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {role.description}
                    </Typography>
                  </Box>
                  {role.isSystem && (
                    <Chip label="Sistema" size="small" color="warning" />
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Usuarios asignados:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AvatarGroup max={4} sx={{ mr: 2 }}>
                      {role.users.map((user) => (
                        <Tooltip key={user.id} title={user.name}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                            {user.avatar}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {role.userCount} usuarios
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {role.permissions.length} permisos asignados
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditRole(role)}
                    disabled={role.isSystem}
                    sx={{ flex: 1, fontWeight: 600, textTransform: 'none' }}
                  >
                    Editar
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRole(role.id)}
                    disabled={role.isSystem}
                    sx={{ color: '#f44336' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          ))
        )}
      </Grid>

      {/* Permissions Matrix */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Matriz de Permisos del Sistema
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Gestiona los permisos de cada rol para las diferentes pantallas del sistema
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
                      Pantalla / Rol
                    </TableCell>
                    {roles.map((role) => (
                      <TableCell key={role.id} align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Chip 
                            label={role.name} 
                            size="small" 
                            sx={{ 
                              backgroundColor: role.color, 
                              color: 'white',
                              mb: 1
                            }} 
                          />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {screens.map((screen) => (
                    <TableRow key={screen.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ mr: 2 }}>
                            {screen.icon === 'admin' && <AdminIcon />}
                            {screen.icon === 'assignment' && <AssignmentIcon />}
                            {screen.icon === 'people' && <PeopleIcon />}
                            {screen.icon === 'visibility' && <VisibilityIcon />}
                            {!['admin', 'assignment', 'people', 'visibility'].includes(screen.icon) && <SettingsIcon />}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {screen.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {screen.route}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {roles.map((role) => {
                        const rolePermission = rolePermissions.find(rp => rp.id === role.id);
                        const screenPermission = rolePermission?.screens?.find(s => s.screenId === screen.id);
                        
                        return (
                          <TableCell key={`${role.id}-${screen.id}`} align="center">
                            <PermissionCell 
                              roleId={role.id}
                              screenId={screen.id}
                              permissions={screenPermission || { canRead: false, canWrite: false, canDelete: false, canAdmin: false }}
                              onPermissionChange={handlePermissionUpdate}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Role Creation/Edit Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa', 
          fontWeight: 700, 
          fontSize: '1.3rem',
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center'
        }}>
          <GroupIcon sx={{ mr: 2 }} />
          {selectedRole ? 'Editar Rol' : 'Crear Nuevo Rol'}
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nombre del Rol"
                value={newRole.name}
                onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Color del Rol"
                type="color"
                value={newRole.color}
                onChange={(e) => setNewRole(prev => ({ ...prev, color: e.target.value }))}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descripción"
                value={newRole.description}
                onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Permisos Asignados
              </Typography>
              
              {Object.entries(groupPermissionsByCategory()).map(([category, categoryPermissions]) => (
                <Accordion key={category} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getCategoryIcon(category)}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        {category}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormGroup>
                      {categoryPermissions.map((permission) => (
                        <FormControlLabel
                          key={permission.id}
                          control={
                            <Checkbox
                              checked={newRole.permissions.includes(permission.name)}
                              onChange={(e) => handlePermissionChange(permission.name, e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {permission.displayName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {permission.description}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={() => setRoleDialogOpen(false)}
            startIcon={<CancelIcon />}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveRole}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            {selectedRole ? 'Actualizar Rol' : 'Crear Rol'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionRoles;
